import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { 
  Users, CheckCircle, AlertCircle, Brain, Target, TrendingUp,
  Layout, Palette, Focus, Lightbulb
} from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  RadarChart, 
  PolarGrid, 
  PolarAngleAxis, 
  PolarRadiusAxis, 
  Radar, 
  ResponsiveContainer,
  Legend
} from 'recharts';

const PublicGridProfileAssessment = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [linkData, setLinkData] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [currentPage, setCurrentPage] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  
  const questionsPerPage = 5;
  const totalPages = Math.ceil(30 / questionsPerPage);

  useEffect(() => {
    loadLinkData();
    loadQuestions();
  }, [token]);

  const loadLinkData = async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/gridprofile/public/${token}`);
      
      if (response.data.completed) {
        setError('Esta avaliação já foi completada.');
        setLoading(false);
        return;
      }
      
      setLinkData(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Erro ao carregar link:', error);
      if (error.response?.status === 404) {
        setError('Link não encontrado ou inválido.');
      } else if (error.response?.status === 403) {
        setError(error.response.data.detail || 'Link expirado ou inativo.');
      } else {
        setError('Erro ao carregar informações da avaliação.');
      }
      setLoading(false);
    }
  };

  const loadQuestions = async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/gridprofile/questions`);
      setQuestions(response.data.questions);
      
      // Initialize answers
      const initialAnswers = {};
      response.data.questions.forEach(q => {
        initialAnswers[q.id] = 3; // Default to neutral
      });
      setAnswers(initialAnswers);
    } catch (error) {
      console.error('Erro ao carregar perguntas:', error);
      setError('Não foi possível carregar as perguntas.');
    }
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      
      const answersArray = Object.keys(answers).map(questionId => ({
        question_id: parseInt(questionId),
        score: answers[questionId]
      }));

      const response = await axios.post(
        `${backendUrl}/api/gridprofile/public/${token}/complete`,
        { answers: answersArray }
      );

      setResult(response.data);
      setCompleted(true);
      
    } catch (error) {
      console.error('Erro ao enviar avaliação:', error);
      setError(error.response?.data?.detail || 'Erro ao enviar respostas. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  const getStyleName = (styleId) => {
    const names = {
      reflexivo: 'Reflexivo',
      cauteloso: 'Cauteloso',
      energetico: 'Energético',
      analitico: 'Analítico',
      organizado: 'Organizado',
      criativo: 'Criativo',
      focado: 'Focado'
    };
    return names[styleId] || styleId;
  };

  const getStyleIcon = (styleId) => {
    const icons = {
      reflexivo: Brain,
      cauteloso: AlertCircle,
      energetico: TrendingUp,
      analitico: Target,
      organizado: Layout,
      criativo: Palette,
      focado: Focus
    };
    const IconComponent = icons[styleId] || Users;
    return <IconComponent className="h-12 w-12" />;
  };

  const getStyleColor = (styleId) => {
    const colors = {
      reflexivo: 'text-blue-600 bg-blue-50 border-blue-200',
      cauteloso: 'text-yellow-600 bg-yellow-50 border-yellow-200',
      energetico: 'text-orange-600 bg-orange-50 border-orange-200',
      analitico: 'text-purple-600 bg-purple-50 border-purple-200',
      organizado: 'text-green-600 bg-green-50 border-green-200',
      criativo: 'text-pink-600 bg-pink-50 border-pink-200',
      focado: 'text-indigo-600 bg-indigo-50 border-indigo-200'
    };
    return colors[styleId] || 'text-gray-600 bg-gray-50 border-gray-200';
  };

  const getStyleDescription = (styleId) => {
    const descriptions = {
      reflexivo: 'Pessoas reflexivas são pensativas e analíticas, preferindo considerar cuidadosamente antes de agir.',
      cauteloso: 'Pessoas cautelosas são cuidadosas e prudentes, avaliando riscos antes de tomar decisões.',
      energetico: 'Pessoas energéticas são dinâmicas e entusiastas, trazendo vitalidade e motivação ao ambiente.',
      analitico: 'Pessoas analíticas são lógicas e detalhistas, focadas em dados e análise precisa.',
      organizado: 'Pessoas organizadas são estruturadas e metódicas, valorizando ordem e planejamento.',
      criativo: 'Pessoas criativas são inovadoras e imaginativas, buscando soluções originais.',
      focado: 'Pessoas focadas são determinadas e concentradas, mantendo atenção em objetivos.'
    };
    return descriptions[styleId] || '';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="animate-spin h-12 w-12 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-gray-600">Carregando avaliação...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-red-200">
          <CardContent className="p-8 text-center">
            <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Erro</h2>
            <p className="text-gray-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (completed && result) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50 flex items-center justify-center p-4">
        <Card className={`max-w-2xl w-full border-2 ${getStyleColor(result.primary_style)}`}>
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className={`p-4 rounded-full ${getStyleColor(result.primary_style)}`}>
                {getStyleIcon(result.primary_style)}
              </div>
            </div>
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <CardTitle className="text-3xl font-bold text-gray-800 mb-2">
              Avaliação Concluída!
            </CardTitle>
            <p className="text-gray-600">Obrigado por completar o Inventário GridProfile</p>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h3 className="text-xl font-semibold mb-4 text-gray-800 text-center">Seu Perfil Comportamental</h3>
              
              {/* Gráfico de Radar */}
              {result.scores && (
                <div className="mb-6">
                  <ResponsiveContainer width="100%" height={350}>
                    <RadarChart data={Object.entries(result.scores).map(([style, score]) => ({
                      estilo: getStyleName(style),
                      valor: parseFloat(score.toFixed(1)),
                      fullMark: 100
                    }))}>
                      <PolarGrid stroke="#e5e7eb" />
                      <PolarAngleAxis 
                        dataKey="estilo" 
                        tick={{ fill: '#374151', fontSize: 11, fontWeight: 600 }}
                      />
                      <PolarRadiusAxis 
                        angle={90} 
                        domain={[0, 100]} 
                        tick={{ fill: '#6b7280', fontSize: 9 }}
                      />
                      <Radar
                        name="Score"
                        dataKey="valor"
                        stroke="#3b82f6"
                        fill="#3b82f6"
                        fillOpacity={0.5}
                        strokeWidth={2}
                      />
                      <Legend 
                        formatter={() => 'Pontuação (%)'} 
                        wrapperStyle={{ paddingTop: '15px' }}
                      />
                    </RadarChart>
                  </ResponsiveContainer>
                  <p className="text-xs text-center text-gray-500 mt-2">
                    Gráfico dos 7 estilos comportamentais
                  </p>
                </div>
              )}
              
              <div className="space-y-4">
                <div className={`p-4 rounded-lg border-2 ${getStyleColor(result.primary_style)}`}>
                  <div className="flex items-center gap-3 mb-2">
                    {getStyleIcon(result.primary_style)}
                    <div>
                      <p className="text-sm text-gray-600">Estilo Primário</p>
                      <p className="text-2xl font-bold">{getStyleName(result.primary_style)}</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-700 mt-3">
                    {getStyleDescription(result.primary_style)}
                  </p>
                </div>

                {result.secondary_style && (
                  <div className={`p-4 rounded-lg border ${getStyleColor(result.secondary_style)}`}>
                    <div className="flex items-center gap-3 mb-2">
                      {getStyleIcon(result.secondary_style)}
                      <div>
                        <p className="text-sm text-gray-600">Estilo Secundário</p>
                        <p className="text-xl font-bold">{getStyleName(result.secondary_style)}</p>
                      </div>
                    </div>
                    <p className="text-sm text-gray-700 mt-3">
                      {getStyleDescription(result.secondary_style)}
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <Lightbulb className="inline h-4 w-4 mr-2" />
                <strong>Nota:</strong> Este resultado é baseado em suas respostas ao questionário. 
                O inventário GridProfile é uma ferramenta lúdica para autoconhecimento, não um teste psicológico validado.
              </p>
            </div>

            <div className="text-center pt-4">
              <p className="text-sm text-gray-600">
                Seus resultados foram enviados para a empresa. 
                Em breve você receberá um relatório completo no email cadastrado.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const currentQuestions = questions.slice(
    currentPage * questionsPerPage,
    (currentPage + 1) * questionsPerPage
  );

  const progress = ((currentPage + 1) / totalPages) * 100;
  const allAnswered = Object.keys(answers).length === questions.length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-blue-100 rounded-full">
                  <Users className="h-8 w-8 text-blue-600" />
                </div>
                <div>
                  <CardTitle className="text-2xl font-bold text-gray-800">
                    Inventário GridProfile
                  </CardTitle>
                  <p className="text-sm text-gray-600">
                    Avaliação de Estilos Comportamentais
                  </p>
                </div>
              </div>
            </div>

            {linkData && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-blue-900">
                  <strong>Participante:</strong> {linkData.participant_name}
                </p>
                <p className="text-sm text-blue-900">
                  <strong>Email:</strong> {linkData.participant_email}
                </p>
              </div>
            )}

            <div className="space-y-2">
              <div className="flex justify-between text-sm text-gray-600">
                <span>Progresso</span>
                <span>{currentPage + 1} de {totalPages} páginas</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          </CardHeader>

          <CardContent className="space-y-6">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h3 className="font-semibold text-yellow-900 mb-2">ℹ️ Instruções</h3>
              <p className="text-sm text-yellow-800">
                Para cada afirmação, indique o quanto você concorda em uma escala de 1 a 5.
                Não há respostas certas ou erradas - responda de acordo com suas preferências naturais.
              </p>
              <div className="flex justify-between text-xs text-yellow-700 mt-3 px-2">
                <span>1 = Discordo Totalmente</span>
                <span>3 = Neutro</span>
                <span>5 = Concordo Totalmente</span>
              </div>
            </div>

            <div className="space-y-4">
              {currentQuestions.map((q, index) => (
                <div key={q.id} className="border rounded-lg p-4 bg-white shadow-sm">
                  <div className="flex items-start gap-3">
                    <span className="font-semibold text-gray-600 min-w-[40px]">
                      {currentPage * questionsPerPage + index + 1}.
                    </span>
                    <div className="flex-1">
                      <p className="mb-4 text-gray-800">{q.question}</p>
                      <div className="flex gap-2 justify-between">
                        {[1, 2, 3, 4, 5].map(score => (
                          <button
                            key={score}
                            onClick={() => setAnswers({...answers, [q.id]: score})}
                            className={`flex-1 px-3 py-3 rounded-lg transition-all font-semibold ${
                              answers[q.id] === score
                                ? 'bg-blue-600 text-white shadow-lg scale-105'
                                : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                            }`}
                          >
                            {score}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-between items-center pt-4">
              <Button
                onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                disabled={currentPage === 0}
                variant="outline"
              >
                ← Anterior
              </Button>

              <div className="text-sm text-gray-600">
                Página {currentPage + 1} de {totalPages}
              </div>

              {currentPage < totalPages - 1 ? (
                <Button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))}
                >
                  Próxima →
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={!allAnswered || submitting}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {submitting ? 'Enviando...' : 'Concluir Avaliação'}
                </Button>
              )}
            </div>

            {currentPage === totalPages - 1 && !allAnswered && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                <p className="text-sm text-yellow-800">
                  <AlertCircle className="inline h-4 w-4 mr-2" />
                  Por favor, responda todas as perguntas antes de concluir.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="text-center mt-6">
          <p className="text-sm text-gray-600">
            Inventário GridProfile - Ferramenta de Autoconhecimento
          </p>
        </div>
      </div>
    </div>
  );
};

export default PublicGridProfileAssessment;
